"use strict";
const moment = require('moment');
const _ = require('lodash');
_.mixin(require('lodash-uuid'));
const { calculateGrossWeightInKg, calculateNetWeightInKg, requiresEMCS, } = require('../../models/article');
const { getTranslation } = require('../../models/translate');
const contentReplacements = {
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
};
const replaceXmlCharacters = string => {
    if (!string)
        return string;
    return string.replace(/[<>&"]/g, character => contentReplacements[character]);
};
const EMCSRecipients = {
    flaivy: {
        exciseNumber: 'SEP5569895468',
        taxWarehouseNumber: '',
        name: 'Flaivy Nation AB',
        address: 'SANDBACKSGATAN 5',
        zipCode: '11621',
        city: 'STOCKHOLM',
    },
    vinden: {
        exciseNumber: 'SEP5569895468',
        taxWarehouseNumber: 'SEP0000036308',
        name: 'Flaivy Nation AB C/O Vinden AB',
        address: 'Mätarvägen 43',
        zipCode: '196 37',
        city: 'Kungsängen',
        deliveryName: 'Flaivy Nation AB C/O Vinden AB',
        deliveryAddress: 'Mätarvägen 43',
        deliveryZipCode: '196 37',
        deliveryCity: 'Kungsängen',
    },
    exacta: {
        exciseNumber: 'SEP5569895468',
        taxWarehouseNumber: 'SEP0000035128',
        name: 'Flaivy Nation AB / Exacta Logistics AB',
        address: 'Verkstadsgatan 5',
        zipCode: '69150',
        city: 'Karlskoga',
        deliveryName: 'Flaivy Nation AB / Exacta Logistics AB',
        deliveryAddress: 'Verkstadsgatan 5',
        deliveryZipCode: '69150',
        deliveryCity: 'Karlskoga',
    },
};
const containerTypeCode = {
    Bottle: 'BO',
    Can: 'CX',
    Keg: 'KG',
    Tetra: 'CT',
    Paper: 'PT',
    BIB: 'BW',
    Bag: 'BG',
    Pet: 'BO',
    Other: 'BO',
};
const consigneeTrader = receiver => `
<ie:ConsigneeTrader language="sv">
    <ie:Traderid>${receiver.exciseNumber}</ie:Traderid>
    <ie:TraderName>${replaceXmlCharacters(receiver.name)}</ie:TraderName>
    <ie:StreetName>${replaceXmlCharacters(receiver.invoiceAddress || receiver.address)}</ie:StreetName>
    <ie:Postcode>${receiver.invoiceZipCode || receiver.zipCode}</ie:Postcode>
    <ie:City>${receiver.invoiceCity || receiver.city}</ie:City>
</ie:ConsigneeTrader>
`;
const consignorTrader = sender => `
<ie:ConsignorTrader language="sv">
    <ie:TraderExciseNumber>${sender.exciseNumber}</ie:TraderExciseNumber>
    <ie:TraderName>${replaceXmlCharacters(sender.name)}</ie:TraderName>
    <ie:StreetName>${replaceXmlCharacters(sender.address)}</ie:StreetName>
    <ie:Postcode>${sender.zipCode}</ie:Postcode>
    <ie:City>${sender.city}</ie:City>
</ie:ConsignorTrader>
`;
const placeOfDispatchTrader = placeOfDispatchData => `
<ie:PlaceOfDispatchTrader language="sv">
    <ie:ReferenceOfTaxWarehouse>${placeOfDispatchData.taxWarehouseNumber}</ie:ReferenceOfTaxWarehouse>
    <ie:TraderName>${replaceXmlCharacters(placeOfDispatchData.name)}</ie:TraderName>
    <ie:StreetName>${replaceXmlCharacters(placeOfDispatchData.address)}</ie:StreetName>
    <ie:Postcode>${placeOfDispatchData.zipCode}</ie:Postcode>
    <ie:City>${placeOfDispatchData.city}</ie:City>
</ie:PlaceOfDispatchTrader>
`;
const deliveryPlaceTrader = customerSubUnit => `
<ie:DeliveryPlaceTrader language="sv">
    <ie:Traderid>${customerSubUnit.taxWarehouseNumber}</ie:Traderid>
    <ie:TraderName>${replaceXmlCharacters(customerSubUnit.deliveryName)}</ie:TraderName>
    <ie:StreetName>${replaceXmlCharacters(customerSubUnit.deliveryAddress)}</ie:StreetName>
    <ie:Postcode>${customerSubUnit.deliveryZipCode}</ie:Postcode>
    <ie:City>${customerSubUnit.deliveryCity}</ie:City>
</ie:DeliveryPlaceTrader>
`;
const orderRowToBodyEAD = (orderRow, index) => {
    const { article } = orderRow;
    return `
<ie:BodyEadEsad>
    <ie:BodyRecordUniqueReference>${index + 1}</ie:BodyRecordUniqueReference>
    <ie:ExciseProductCode>${article.exciseProductCode}</ie:ExciseProductCode>
    <ie:CnCode>${article.knNumber}</ie:CnCode>
    <ie:Quantity>${_.round(orderRow.deliveredQuantity * orderRow.article.volume, 3)}</ie:Quantity>
    <ie:GrossMass>${(calculateGrossWeightInKg(article) * orderRow.deliveredQuantity).toFixed(2)}
    </ie:GrossMass>
    <ie:NetMass>${(calculateNetWeightInKg(article) * orderRow.deliveredQuantity).toFixed(2)}</ie:NetMass>
    <ie:AlcoholicStrengthByVolumeInPercentage>${article.alcoholPercentage}</ie:AlcoholicStrengthByVolumeInPercentage>
    <ie:FiscalMarkUsedFlag>0</ie:FiscalMarkUsedFlag>
    <ie:CommercialDescription language="sv">${replaceXmlCharacters(article.shortName)}</ie:CommercialDescription>
    <ie:Package>
    <ie:KindOfPackages>${containerTypeCode[article.container] || ''}</ie:KindOfPackages>
    <ie:NumberOfPackages>${orderRow.deliveredQuantity}</ie:NumberOfPackages>
    </ie:Package>
</ie:BodyEadEsad>
`;
};
const shouldRowBeIncludedOnEAD = row => row.article &&
    !row.article.utilityArticle &&
    requiresEMCS(row.article) &&
    !row.article.alcoholTaxPaid;
const getRowsToIncludeOnEAD = orderRows => orderRows.filter(shouldRowBeIncludedOnEAD);
const getBodyEADs = orderRows => getRowsToIncludeOnEAD(orderRows).map((row, index) => orderRowToBodyEAD(row, index));
const getMissingPropsForEmcsOrderRows = (rows, additionalProps = []) => {
    const articleRequiredProperties = [
        'orderedQuantity',
        'article.name',
        'article.shortName',
        'article.alcoholPercentage',
        'article.container',
        'article.volume',
        'article.colliSize',
        'article.knNumber',
        'article.exciseProductCode',
        ...additionalProps,
    ];
    const missingProps = [];
    for (const [index, row] of rows.entries()) {
        if (shouldRowBeIncludedOnEAD(row)) {
            const missingFromRow = articleRequiredProperties
                .filter(requiredProperty => !_.get(row, requiredProperty, null))
                .map(missingProp => ({
                value: `orderRows[${index}].${missingProp}`,
                label: `Orderrad ${index + 1} saknar ${getTranslation(_.includes(missingProp, '.')
                    ? missingProp
                    : `order.orderRows.${missingProp}`)}`,
            }));
            missingProps.push(...missingFromRow);
        }
    }
    return missingProps;
};
const validateOrderForEmcs = order => {
    const orderRequiredProperties = order.customerSubUnit
        ? [
            'customerSubUnit.deliveryName',
            'customerSubUnit.deliveryAddress',
            'customerSubUnit.deliveryZipCode',
            'customerSubUnit.deliveryCity',
            'customerSubUnit.exciseNumber',
            'customerSubUnit.taxWarehouseNumber',
        ]
        : [
            'supplierCustomer.deliveryName',
            'supplierCustomer.deliveryAddress',
            'supplierCustomer.deliveryZipCode',
            'supplierCustomer.deliveryCity',
            'supplierCustomer.exciseNumber',
            'supplierCustomer.taxWarehouseNumber',
        ];
    const missingPropsForOrder = orderRequiredProperties
        .filter(requiredProperty => !_.get(order, requiredProperty, null))
        .map(x => ({
        value: x,
        label: getTranslation(x),
    }));
    const missingPropsForRows = getMissingPropsForEmcsOrderRows(order.orderRows, [
        'name',
    ]);
    const missingProps = [...missingPropsForOrder, ...missingPropsForRows];
    return { isValid: _.isEmpty(missingProps), missingProps };
};
const validatePurchaseOrderForEmcs = purchaseOrder => {
    const missingProps = getMissingPropsForEmcsOrderRows(purchaseOrder.purchaseOrderRows);
    return { isValid: _.isEmpty(missingProps), missingProps };
};
const getTargetTaxWarehouseNumber = decoratedPurchaseOrder => {
    const { targetWarehouse } = decoratedPurchaseOrder;
    if (targetWarehouse === 'exacta') {
        return EMCSRecipients.exacta.taxWarehouseNumber;
    }
    return (decoratedPurchaseOrder.taxWarehouseNumberAtVinden ||
        EMCSRecipients.vinden.taxWarehouseNumber);
};
module.exports = {
    validateOrderForEmcs,
    validatePurchaseOrderForEmcs,
    createOrderEmcsFile: order => `
  <ie:IE815 xmlns:ie="urn:publicid:-:EC:DGTAXUD:EMCS:PHASE4:IE815:V3.13" xmlns:tms="urn:publicid:-:EC:DGTAXUD:EMCS:PHASE4:TMS:V3.13">
  <ie:Header>
    <tms:MessageSender>NDEA.SE</tms:MessageSender>
    <tms:MessageRecipient>NDEA.SE</tms:MessageRecipient>
    <tms:DateOfPreparation>${moment().format('YYYY-MM-DD')}</tms:DateOfPreparation>
    <tms:TimeOfPreparation>${moment().format('HH:mm:ss')}</tms:TimeOfPreparation>
    <tms:MessageIdentifier>${_.uuid()}</tms:MessageIdentifier>
  </ie:Header>
  <ie:Body>
    <ie:SubmittedDraftOfEADESAD>
      <ie:Attributes>
        <ie:SubmissionMessageType>1</ie:SubmissionMessageType>
        <ie:DeferredSubmissionFlag>0</ie:DeferredSubmissionFlag>
      </ie:Attributes>
      ${consigneeTrader(order.customerSubUnit || order.supplierCustomer)}
      ${consignorTrader(order.orderFullfillmentCenter)}
      ${placeOfDispatchTrader(order.orderFullfillmentCenter)}
      ${deliveryPlaceTrader(order.customerSubUnit || order.supplierCustomer)}
      <ie:CompetentAuthorityDispatchOffice>
        <ie:ReferenceNumber>SE900001</ie:ReferenceNumber>
      </ie:CompetentAuthorityDispatchOffice>
      <ie:DocumentCertificate>
        <ie:DocumentType>C624</ie:DocumentType>
        <ie:DocumentReference>123</ie:DocumentReference>
        <ie:DocumentDescription language="sv">Beskrivning av dokumentet</ie:DocumentDescription>
        <ie:ReferenceOfDocument language="sv">Referens till dokumentet</ie:ReferenceOfDocument>
      </ie:DocumentCertificate>
      <ie:HeaderEadEsad>
        <ie:DestinationTypeCode>1</ie:DestinationTypeCode>
        <ie:JourneyTime>D01</ie:JourneyTime>
        <ie:TransportArrangement>1</ie:TransportArrangement>
      </ie:HeaderEadEsad>
      <ie:TransportMode>
        <ie:TransportModeCode>3</ie:TransportModeCode>
      </ie:TransportMode>
      <ie:MovementGuarantee>
        <ie:GuarantorTypeCode>1</ie:GuarantorTypeCode>
      </ie:MovementGuarantee>
      ${getBodyEADs(order.orderRows).join('')}
      <ie:EadEsadDraft>
        <ie:LocalReferenceNumber>${order.yourOrderNumber || order.orderNumber}</ie:LocalReferenceNumber>
        <ie:InvoiceNumber>${order.advanceInvoiceReference || order.orderNumber}</ie:InvoiceNumber>
        <ie:OriginTypeCode>1</ie:OriginTypeCode>
        <ie:DateOfDispatch>${moment().format('YYYY-MM-DD')}</ie:DateOfDispatch>
      </ie:EadEsadDraft>
      <ie:TransportDetails>
        <ie:TransportUnitCode>2</ie:TransportUnitCode>
        <ie:IdentityOfTransportUnits>PostNord</ie:IdentityOfTransportUnits>
      </ie:TransportDetails>
    </ie:SubmittedDraftOfEADESAD>
  </ie:Body>
</ie:IE815>
  `,
    createPurchaseOrderEmcsFile: decoratedPurchaseOrder => {
        const { targetWarehouse } = decoratedPurchaseOrder;
        const deliveryPlaceTraderWithOwnTaxWarehouseNumber = {
            ...EMCSRecipients[targetWarehouse],
            taxWarehouseNumber: getTargetTaxWarehouseNumber(decoratedPurchaseOrder),
        };
        const suppliersEndCustomerInfo = {
            exciseNumber: decoratedPurchaseOrder.dispatcherExciseNumber,
            taxWarehouseNumber: decoratedPurchaseOrder.dispatcherTaxWarehouseNumber,
            name: decoratedPurchaseOrder.dispatcherName,
            address: decoratedPurchaseOrder.dispatcherAddress,
            zipCode: decoratedPurchaseOrder.dispatcherZipCode,
            city: decoratedPurchaseOrder.dispatcherCity,
        };
        // eslint-disable-next-line no-param-reassign
        decoratedPurchaseOrder.purchaseOrderRows =
            decoratedPurchaseOrder.purchaseOrderRows.map(x => ({
                ...x,
                deliveredQuantity: x.orderedQuantity,
            }));
        return `
  <ie:IE815 xmlns:ie="urn:publicid:-:EC:DGTAXUD:EMCS:PHASE4:IE815:V3.13" xmlns:tms="urn:publicid:-:EC:DGTAXUD:EMCS:PHASE4:TMS:V3.13">
  <ie:Header>
    <tms:MessageSender>NDEA.SE</tms:MessageSender>
    <tms:MessageRecipient>NDEA.SE</tms:MessageRecipient>
    <tms:DateOfPreparation>${moment().format('YYYY-MM-DD')}</tms:DateOfPreparation>
    <tms:TimeOfPreparation>${moment().format('HH:mm:ss')}</tms:TimeOfPreparation>
    <tms:MessageIdentifier>${_.uuid()}</tms:MessageIdentifier>
  </ie:Header>
  <ie:Body>
    <ie:SubmittedDraftOfEADESAD>
      <ie:Attributes>
        <ie:SubmissionMessageType>1</ie:SubmissionMessageType>
        <ie:DeferredSubmissionFlag>0</ie:DeferredSubmissionFlag>
      </ie:Attributes>
      ${consigneeTrader(EMCSRecipients.flaivy)}
      ${consignorTrader(suppliersEndCustomerInfo)}
      ${placeOfDispatchTrader(suppliersEndCustomerInfo)}
      ${deliveryPlaceTrader(deliveryPlaceTraderWithOwnTaxWarehouseNumber)}
      <ie:CompetentAuthorityDispatchOffice>
        <ie:ReferenceNumber>SE900001</ie:ReferenceNumber>
      </ie:CompetentAuthorityDispatchOffice>
      <ie:DocumentCertificate>
        <ie:DocumentType>C624</ie:DocumentType>
        <ie:DocumentReference>123</ie:DocumentReference>
        <ie:DocumentDescription language="sv">Beskrivning av dokumentet</ie:DocumentDescription>
        <ie:ReferenceOfDocument language="sv">Referens till dokumentet</ie:ReferenceOfDocument>
      </ie:DocumentCertificate>
      <ie:HeaderEadEsad>
        <ie:DestinationTypeCode>1</ie:DestinationTypeCode>
        <ie:JourneyTime>D01</ie:JourneyTime>
        <ie:TransportArrangement>1</ie:TransportArrangement>
      </ie:HeaderEadEsad>
      <ie:TransportMode>
        <ie:TransportModeCode>3</ie:TransportModeCode>
      </ie:TransportMode>
      <ie:MovementGuarantee>
        <ie:GuarantorTypeCode>1</ie:GuarantorTypeCode>
      </ie:MovementGuarantee>
      ${getBodyEADs(decoratedPurchaseOrder.purchaseOrderRows).join('')}
      <ie:EadEsadDraft>
        <ie:LocalReferenceNumber>${decoratedPurchaseOrder.supplierOrderNumber ||
            decoratedPurchaseOrder.orderNumber}</ie:LocalReferenceNumber>
        <ie:InvoiceNumber>${decoratedPurchaseOrder.orderNumber}</ie:InvoiceNumber>
        <ie:OriginTypeCode>1</ie:OriginTypeCode>
        <ie:DateOfDispatch>${moment().format('YYYY-MM-DD')}</ie:DateOfDispatch>
      </ie:EadEsadDraft>
      <ie:TransportDetails>
        <ie:TransportUnitCode>2</ie:TransportUnitCode>
        <ie:IdentityOfTransportUnits>PostNord</ie:IdentityOfTransportUnits>
      </ie:TransportDetails>
    </ie:SubmittedDraftOfEADESAD>
  </ie:Body>
</ie:IE815>
  `;
    },
};
